import { FC } from 'react'
import { Modal } from 'react-bootstrap'

type TableCombination = {
  tableIds: number[]
  tableNumbers: string[]
  tableCapacities?: number[] // Capacidades individuais de cada mesa
  totalCapacity: number
  wastePercentage: number
  allocationMethod: string
}

type SuggestionData = {
  hasAvailableTables: boolean
  suggestions: TableCombination[]
  environment: {
    id: number
    name: string
  }
  booking: {
    id: number
    name: string
    people: number
    hour: string
    date: string
  }
}

type TableSuggestionModalProps = {
  show: boolean
  onClose: () => void
  onAccept: (suggestion: TableCombination) => void
  onConfirmWithoutTable?: () => Promise<void>
  suggestions: SuggestionData | null
}

export const TableSuggestionModal: FC<TableSuggestionModalProps> = ({
  show,
  onClose,
  onAccept,
  onConfirmWithoutTable,
  suggestions,
}) => {
  if (!suggestions) return null

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Sugestão de Alocação de Mesa</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-4 p-3 bg-light rounded">
          <div className="row g-3">
            <div className="col-md-6">
              <div>
                <h6 className="mb-1">
                  <i className='bi bi-person-fill me-2 text-primary'></i>
                  Reserva
                </h6>
                <p className="mb-0 text-muted">
                  <strong>{suggestions.booking.name}</strong>
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div>
                <h6 className="mb-1">
                  <i className='bi bi-clock-fill me-2 text-primary'></i>
                  Horário
                </h6>
                <p className="mb-0 text-muted">
                  {suggestions.booking.hour} | {suggestions.booking.date}
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div>
                <h6 className="mb-1">
                  <i className='bi bi-people-fill me-2 text-primary'></i>
                  Pessoas
                </h6>
                <p className="mb-0 text-muted">
                  <strong>{suggestions.booking.people}</strong> clientes
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div>
                <h6 className="mb-1">
                  <i className='bi bi-building me-2 text-primary'></i>
                  Ambiente
                </h6>
                <p className="mb-0 text-muted">
                  {suggestions.environment.name}
                </p>
              </div>
            </div>
          </div>
        </div>

        {suggestions.hasAvailableTables ? (
          <>
            <h6 className="mb-3">
              <i className='bi bi-list-check me-2 text-success'></i>
              Selecione uma opção de mesa:
            </h6>
            <div className='row g-3'>
              {suggestions.suggestions.map((suggestion, index) => (
                <div key={index} className='col-lg-6'>
                  <div className="card h-100 border-light shadow-sm" style={{ transition: 'transform 0.2s, box-shadow 0.2s', display: 'flex', flexDirection: 'column' }}>
                    <div className="card-body d-flex flex-column p-3" style={{ justifyContent: 'space-between', height: '100%' }}>
                      <div>
                        <h6 className="mb-2 fw-bold text-primary small">
                          <i className='bi bi-option me-2'></i>
                          Opção {index + 1}
                        </h6>
                        
                        <div style={{ fontSize: '0.925rem' }}>
                          <div className="mb-2">
                            <label className="form-label mb-1 small text-muted" style={{ fontSize: '0.8rem' }}>
                              <strong>Mesas</strong>
                            </label>
                            <p className="mb-0 fw-semibold text-break" style={{ fontSize: '0.925rem', lineHeight: '1.4' }}>
                              {suggestion.tableCapacities && suggestion.tableCapacities.length > 0
                                ? suggestion.tableNumbers.map((num, idx) => 
                                    `${num} (${suggestion.tableCapacities![idx]} lugares)`
                                  ).join(', ')
                                : suggestion.tableNumbers.join(', ')}
                            </p>
                          </div>
                          
                          <div className="mb-2">
                            <label className="form-label mb-1 small text-muted" style={{ fontSize: '0.8rem' }}>
                              <strong>Capacidade</strong>
                            </label>
                            <p className="mb-0 fw-bold text-info" style={{ fontSize: '1rem' }}>
                              {suggestion.totalCapacity} lugares
                            </p>
                          </div>

                          <div>
                            <label className="form-label mb-1 small text-muted" style={{ fontSize: '0.8rem' }}>
                              <strong>Desperdício</strong>
                            </label>
                            <p className="mb-0">
                              <span
                                className="badge"
                                style={{
                                  fontSize: '0.8rem',
                                  padding: '0.35rem 0.55rem',
                                  backgroundColor:
                                    suggestion.wastePercentage <= 20
                                      ? '#d4edda'
                                      : suggestion.wastePercentage <= 40
                                      ? '#fff3cd'
                                      : '#f8d7da',
                                  color:
                                    suggestion.wastePercentage <= 20
                                      ? '#155724'
                                      : suggestion.wastePercentage <= 40
                                      ? '#856404'
                                      : '#721c24',
                                  fontWeight: '500'
                                }}
                              >
                                {suggestion.wastePercentage}% ({Math.round((suggestion.totalCapacity * suggestion.wastePercentage) / 100)} vazio{Math.round((suggestion.totalCapacity * suggestion.wastePercentage) / 100) !== 1 ? 's' : ''})
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>

                      <button
                        className="btn btn-primary"
                        style={{ 
                          padding: '0.3rem 0.65rem', 
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          borderRadius: '0.2rem',
                          alignSelf: 'flex-end',
                          marginTop: '1rem',
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => {
                          console.log('🔘 Botão Selecionar clicado', suggestion)
                          onAccept(suggestion)
                        }}
                      >
                        <i className='bi bi-check-circle me-1' style={{ fontSize: '0.65rem' }}></i>
                        Selecionar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="alert alert-warning d-flex align-items-center" role="alert">
            <i className='bi bi-exclamation-triangle me-2'></i>
            <div>
              Não há mesas disponíveis no momento para esta reserva.
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button 
          className="btn btn-info" 
          onClick={async () => {
            console.log('🎯 Botão "Recusar e confirmar sem mesa" clicado')
            if (onConfirmWithoutTable) {
              await onConfirmWithoutTable()
            }
            onClose()
          }}
        >
          {suggestions.hasAvailableTables
            ? 'Recusar e confirmar sem mesa'
            : 'Confirmar sem mesa'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}
